// Ported from dev-scrips-pm-react src/documents/components/documents-filter-panel.tsx (PROD-1119 cross-module closure).
import { useEffect, useId, useState } from 'react';
import { Check } from 'lucide-react';
import { Button, DatePicker, Drawer, Segmented, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { dateToYmd, ymdToLocalDate } from '@/shared/lib/local-date';
import { cn } from '@/lib/utils';
import { DocumentType, type DocumentTypeCode, type UploadedByData } from '@/documents/types/document';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

/**
 * DocumentsFilterPanel — port of Flutter `documents_filter_widget.dart`.
 *
 * Three sections (verbatim from Flutter):
 *   1. Time period — start / end date pickers + per-section Reset
 *   2. File type   — multi-select from 5 hardcoded Flutter-verbatim
 *                    types (Notes / Labs / Radiology / Procedure /
 *                    Admin Referrals — Flutter intentionally omits
 *                    `Forms` from the filter list)
 *   3. Uploaded by — multi-select populated from `useDocumentUploadedBy`
 *
 * State strategy: draft state owned by this Sheet (mirrors the
 * canonical billing claim-workbench-filter-sheet). Apply commits via
 * `onApply`; close-without-apply discards in-flight edits.
 *
 * Date format on the wire: no-Z ISO (`YYYY-MM-DD`). The hook
 * (`usePatientDocuments`) normalises to that format before POST.
 */

export interface DocumentsFilterValue {
  /** ISO date string `YYYY-MM-DD`. */
  startDate?: string;
  /** ISO date string `YYYY-MM-DD`. */
  endDate?: string;
  /** Selected file-type wire literals. */
  fileType: DocumentTypeCode[];
  /** Selected uploader user-ids. */
  uploadedBy: string[];
  /**
   * PROD-900 — toggle between Active and Archived lists. Defaults to
   * `false` (Active) so existing callers keep the same default behaviour.
   * Flipping to `true` surfaces archived rows (which then offer the
   * "Unarchive" menu item).
   */
  isArchived?: boolean;
}

export const EMPTY_DOCUMENTS_FILTERS: DocumentsFilterValue = {
  startDate: undefined,
  endDate: undefined,
  fileType: [],
  uploadedBy: [],
  isArchived: false,
};

/**
 * Document types surfaced in the filter — Flutter `documentTypesList`
 * (`documents_filter_widget.dart:35-41`). 5 entries; `Forms` is
 * intentionally absent.
 */
export const DOCUMENT_TYPE_FILTER_OPTIONS: ReadonlyArray<{
  label: string;
  value: DocumentTypeCode;
}> = [
  { label: 'Notes', value: DocumentType.Notes },
  { label: 'Labs', value: DocumentType.Labs },
  { label: 'Radiology', value: DocumentType.Radiology },
  { label: 'Procedure', value: DocumentType.Procedure },
  { label: 'Admin & Referrals', value: DocumentType.AdminReferrals },
];

interface DocumentsFilterPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: DocumentsFilterValue;
  onApply: (next: DocumentsFilterValue) => void;
  /** Uploader options — passed from `useDocumentUploadedBy(patientId)`. */
  uploaders?: UploadedByData[];
  /** True while the uploader query is loading. */
  uploadersLoading?: boolean;
  /**
   * PROD-900 follow-up: hide the Status (Active / Archived) section
   * when the consumer surface already exposes Status as an inline
   * segmented control (e.g. patient-chart Documents tab). Defaults to
   * false so existing consumers keep the section.
   */
  hideStatusSection?: boolean;
}

export function DocumentsFilterPanel({
  open,
  onOpenChange,
  value,
  onApply,
  uploaders,
  uploadersLoading,
  hideStatusSection = false,
}: DocumentsFilterPanelProps) {
  const [draft, setDraft] = useState<DocumentsFilterValue>(value);

  // Reset draft to incoming value whenever the sheet opens — discards
  // any half-finished edit from a previous open-close cycle.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const hasDates = !!(draft.startDate || draft.endDate);
  const hasTypes = draft.fileType.length > 0;
  const hasUploaders = draft.uploadedBy.length > 0;

  const toggleType = (token: DocumentTypeCode) => {
    setDraft((d) => {
      const next = new Set(d.fileType);
      if (next.has(token)) next.delete(token);
      else next.add(token);
      return { ...d, fileType: Array.from(next) as DocumentTypeCode[] };
    });
  };

  const toggleUploader = (userId: string) => {
    setDraft((d) => {
      const next = new Set(d.uploadedBy);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return { ...d, uploadedBy: Array.from(next) };
    });
  };

  const resetDates = () => setDraft((d) => ({ ...d, startDate: undefined, endDate: undefined }));
  const resetTypes = () => setDraft((d) => ({ ...d, fileType: [] }));
  const resetUploaders = () => setDraft((d) => ({ ...d, uploadedBy: [] }));

  const onStartChange = (next?: string) => {
    setDraft((d) => {
      // Flutter parity (documents_filter_widget.dart:139-143): if the
      // newly-picked start date is AFTER the existing end date, clear
      // end so the next pick can re-establish a valid range.
      const shouldClearEnd =
        next && d.endDate ? d.endDate < next : false;
      return {
        ...d,
        startDate: next,
        endDate: shouldClearEnd ? undefined : d.endDate,
      };
    });
  };

  const onEndChange = (next?: string) => {
    setDraft((d) => ({ ...d, endDate: next }));
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <Drawer
      open={open}
      onClose={() => onOpenChange(false)}
      side="right"
      width={420}
      title="Filters"
      className="bg-surface-background"
      // The design's drawer footer (chart-documents-filters.html): Reset all (secondary) and Apply filters (primary).
      footer={
        <div data-slot="filter-drawer-footer">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setDraft(EMPTY_DOCUMENTS_FILTERS);
              onApply(EMPTY_DOCUMENTS_FILTERS);
              onOpenChange(false);
            }}
          >
            Reset all
          </Button>
          <Button
            type="button"
            onClick={() => {
              onApply(draft);
              onOpenChange(false);
            }}
          >
            Apply filters
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Section 1 — Time period */}
          <Section
            label="Time Period"
            onReset={hasDates ? resetDates : undefined}
          >
            <div className="grid grid-cols-2 gap-3">
              <DateInput
                ariaLabel="Start date"
                value={draft.startDate}
                onChange={onStartChange}
                max={today}
              />
              <DateInput
                ariaLabel="End date"
                value={draft.endDate}
                onChange={onEndChange}
                min={draft.startDate}
                max={today}
              />
            </div>
          </Section>

          {/* Section 2 — File type */}
          <Section
            label="File Type"
            onReset={hasTypes ? resetTypes : undefined}
          >
            <ul className="space-y-2">
              {DOCUMENT_TYPE_FILTER_OPTIONS.map((opt) => (
                <li key={opt.value}>
                  <CheckRow
                    label={opt.label}
                    checked={draft.fileType.includes(opt.value)}
                    onToggle={() => toggleType(opt.value)}
                  />
                </li>
              ))}
            </ul>
          </Section>

          {/* Section 3 — Archive state (PROD-900). Two-state segmented
              toggle: Active (default) vs Archived. Three-state "All"
              would need a backend wire change (current `isArchived`
              query param is bool-only), out of scope for V1.
              Hidden when the consumer surface exposes Status inline
              (e.g. chart-tab) — see `hideStatusSection` prop. */}
          {hideStatusSection ? null : (
            <Section
              label="Status"
              onReset={
                draft.isArchived ? () => setDraft((d) => ({ ...d, isArchived: false })) : undefined
              }
            >
              <Segmented<'active' | 'archived'>
                aria-label="Document status"
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'archived', label: 'Archived' },
                ]}
                value={draft.isArchived ? 'archived' : 'active'}
                onChange={(v) =>
                  setDraft((d) => ({ ...d, isArchived: v === 'archived' }))
                }
              />
            </Section>
          )}

          {/* Section 4 — Uploaded by */}
          <Section
            label="Uploaded By"
            onReset={hasUploaders ? resetUploaders : undefined}
          >
            {uploadersLoading ? (
              <BrandLoading size={32} label="Loading uploaders…" />
            ) : !uploaders || uploaders.length === 0 ? (
              <p className="text-sm text-text-muted">
                No uploaders to filter by yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {uploaders.map((u) => {
                  const id = u.userId ?? '';
                  const name = u.name ?? '';
                  if (!id) return null;
                  return (
                    <li key={id}>
                      <CheckRow
                        label={name || '(unnamed)'}
                        checked={draft.uploadedBy.includes(id)}
                        onToggle={() => toggleUploader(id)}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>
      </div>
    </Drawer>
  );
}

function Section({
  label,
  onReset,
  children,
}: {
  label: string;
  /** Pass `undefined` to render Reset disabled (matches Flutter's
   *  disabled-state when there's nothing to clear). */
  onReset?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wide text-text-muted">
          {label}
        </span>
        <button
          type="button"
          onClick={onReset}
          disabled={!onReset}
          className={cn(
            'text-sm font-medium',
            onReset
              ? 'text-brand-primary hover:text-brand-depth'
              : 'text-text-hint cursor-default',
          )}
        >
          Reset
        </button>
      </div>
      {children}
    </div>
  );
}

function CheckRow({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2 text-left"
    >
      <span
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded-full border transition-colors',
          checked
            ? 'border-brand-primary bg-brand-primary'
            : 'border-border-default bg-transparent',
        )}
      >
        {checked ? <Check className="size-3 text-white" aria-hidden /> : null}
      </span>
      <span className="text-sm text-text-primary">{label}</span>
    </button>
  );
}

function DateInput({
  ariaLabel,
  value,
  onChange,
  min,
  max,
}: {
  ariaLabel: string;
  value?: string;
  onChange: (next?: string) => void;
  min?: string;
  max?: string;
}) {
  // Accessible name: the DS DatePicker trigger shows the formatted date once picked, so a
  // placeholder alone loses the field's name (Codex CONFIRMED). The trigger button carries
  // `id`, so an sr-only label keeps "Start date"/"End date" announced at all times.
  const pickerId = useId();
  return (
    <>
      <label htmlFor={pickerId} className="sr-only">
        {ariaLabel}
      </label>
      <DatePicker
        id={pickerId}
        value={ymdToLocalDate(value)}
        onChange={(d) => onChange(dateToYmd(d) || undefined)}
        minDate={ymdToLocalDate(min)}
        maxDate={ymdToLocalDate(max)}
        placeholder={ariaLabel}
      />
    </>
  );
}
